/**
 * EXECUTION BARRIER MAP.
 *
 * One question: WHAT PREVENTS THIS WORK FROM EXECUTING?
 *
 * Everything on this panel is a backend fact rendered in the backend's own
 * order. It computes no state, ranks nothing, and never answers with a single
 * package-level verdict — `executable-work-package-1` gives a status per
 * INSTANCE, and fusing six states into one would be a state machine living in
 * React with a precedence rule no contract owns.
 *
 * Phone-first: everything stacks. No three-column table of conditions, because
 * the person most likely to read this is standing at a workface.
 */
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import {
  RuntimeStateBadge,
  TruthStateBadge,
  EvidenceReference,
  MissingInformationCallout,
  ContractMismatchCallout,
} from "./RuntimePrimitives";
import { BARRIER_CONTRACTS, presentBarrierAvailability } from "./barrierPresentation";
import { OctagonAlert, AlertTriangle, HelpCircle, GitCompareArrows, RefreshCw } from "lucide-react";

/** The four groups, each with the reason it is its own group. */
const CONDITION_GROUPS = [
  {
    key: "blocking",
    title: "Blocking",
    icon: OctagonAlert,
    tone: "text-red-400",
    blurb: "A hard condition stands. This work cannot start until it is met.",
  },
  {
    key: "conditional",
    title: "Conditions outstanding",
    icon: AlertTriangle,
    tone: "text-amber-400",
    blurb: "Nothing blocks outright, but these are not satisfied yet.",
  },
  {
    key: "unknown",
    title: "Not established",
    icon: HelpCircle,
    tone: "text-muted-foreground",
    blurb: "These apply and no source answers them. Nothing has been assumed.",
  },
  {
    key: "conflicts",
    title: "Sources disagree",
    icon: GitCompareArrows,
    tone: "text-amber-400",
    blurb: "More than one source answers, and they do not agree. OMNΛS picks neither.",
  },
];

const ConditionRow = ({ condition }) => (
  <div className="flex flex-wrap items-start gap-x-2 gap-y-1 min-w-0 py-1">
    <TruthStateBadge state={condition.state} className="shrink-0" />
    <span className="text-[12px] break-words min-w-0 flex-1">
      <span className="text-foreground">{condition.gate}</span>
      {condition.detail ? <span className="text-muted-foreground"> — {condition.detail}</span> : null}
    </span>
    <EvidenceReference sourceTable={condition.sourceTable} sourceIds={condition.sourceIds} />
  </div>
);

const InstanceCard = ({ instance }) => {
  const groups = CONDITION_GROUPS.map((g) => ({ ...g, rows: instance.conditions[g.key] })).filter(
    (g) => g.rows.length > 0
  );
  return (
    <div
      className="rounded-lg border border-border/60 bg-muted/10 p-3 space-y-2 min-w-0"
      data-testid="barrier-instance"
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <p className="text-sm font-medium break-words min-w-0">
          {instance.instanceName || instance.instanceId}
        </p>
        <RuntimeStateBadge state={instance.status} className="shrink-0" />
      </div>

      {/* The engine's own naming of what governs this instance. */}
      {instance.governingConstraint && (
        <p className="text-[11px] text-muted-foreground break-words">
          Governing condition: {instance.governingConstraint}
        </p>
      )}

      {groups.map((g) => (
        <div key={g.key} className="space-y-0.5">
          <p className={`text-[11px] font-medium flex items-center gap-1 ${g.tone}`}>
            <g.icon className="w-3 h-3 shrink-0" aria-hidden="true" />
            {g.title}
            <span className="text-muted-foreground font-normal">({g.rows.length})</span>
          </p>
          {g.rows.map((c, i) => (
            <ConditionRow key={`${c.gate}-${i}`} condition={c} />
          ))}
        </div>
      ))}

      {groups.length === 0 && (
        <p className="text-[11px] text-muted-foreground">
          No condition on this instance is failing, outstanding, unanswered or disputed.
        </p>
      )}

      <MissingInformationCallout items={instance.missingFacts} title="Not established here" />

      {/* An unknown ERS is not a date. Saying so beats an empty cell. */}
      <p className="text-[10px] text-muted-foreground">
        {instance.ersKnown
          ? `Earliest realistic start: ${instance.earliestRealisticStart}`
          : "Earliest realistic start is not established."}
      </p>
    </div>
  );
};

/**
 * One constraint and what the backend says resolving it buys.
 *
 * Every figure and every caveat is the contract's. The caveats are rendered,
 * not summarised — each one states a limit on its own number, and that is
 * precisely the sentence a UI is tempted to drop.
 */
const ConstraintRow = ({ constraint }) => (
  <div
    className="rounded-lg border border-border/60 bg-muted/20 p-3 space-y-1.5 min-w-0"
    data-testid="barrier-constraint"
  >
    <div className="flex flex-wrap items-start justify-between gap-2">
      <p className="text-sm font-medium break-words min-w-0">
        {constraint.title || constraint.constraintKey}
      </p>
      {constraint.hard && (
        <span className="text-[10px] text-red-400 border border-red-500/30 rounded px-1.5 py-0.5 shrink-0">
          HARD
        </span>
      )}
    </div>

    {constraint.holdsNow.count !== null && (
      <p className="text-[11px]">
        <span className="text-muted-foreground">Holds now: </span>
        {constraint.holdsNow.count} work {constraint.holdsNow.count === 1 ? "front" : "fronts"}
      </p>
    )}

    {constraint.unlocks.count !== null && (
      <p className="text-[11px] border-l-2 border-border pl-2">
        <span className="text-muted-foreground">If resolved alone, strictly unlocks: </span>
        {constraint.unlocks.count}
        {constraint.unlocks.note && (
          <span className="block text-[10px] text-muted-foreground italic">
            {constraint.unlocks.note}
          </span>
        )}
      </p>
    )}

    {constraint.soleHardFailedGateOf.count !== null && (
      <p className="text-[11px] text-muted-foreground">
        Only hard failure on: {constraint.soleHardFailedGateOf.count}
        {constraint.soleHardFailedGateOf.note && (
          <span className="block text-[10px] italic">{constraint.soleHardFailedGateOf.note}</span>
        )}
      </p>
    )}

    {constraint.downstreamExposure.count !== null && (
      <p className="text-[11px] text-muted-foreground border-l-2 border-border/50 pl-2">
        Downstream exposure: {constraint.downstreamExposure.count}
        <span className="block text-[10px] italic">
          {constraint.downstreamExposure.note ||
            "Upper bound over declared dependency edges."}{" "}
          It is not a statement about what is causing what, and not an instruction about what to
          resolve first.
        </span>
      </p>
    )}

    {constraint.unknownHolds.count > 0 && (
      <p className="text-[11px] text-muted-foreground">
        Also holds {constraint.unknownHolds.count} through conditions nobody has answered —
        resolving this cannot be promised to release those.
      </p>
    )}

    <EvidenceReference sourceTable={constraint.sourceTable} sourceIds={constraint.sourceIds} />
  </div>
);

/** A conflicted or unknown-only constraint. Identity only, by contract. */
const ReducedRow = ({ constraint, note }) => (
  <div className="rounded border border-border/50 bg-muted/10 p-2 min-w-0" data-testid="barrier-reduced">
    <p className="text-[12px] break-words">{constraint.title || constraint.constraintKey}</p>
    {constraint.touches.count !== null && (
      <p className="text-[10px] text-muted-foreground">Touches {constraint.touches.count}</p>
    )}
    <p className="text-[10px] text-muted-foreground italic">{note}</p>
  </div>
);

const ExecutionBarrierMap = ({ barrier, contractVersion, loading, error, onRetry }) => {
  if (loading) {
    return (
      <Card className="border-border/60">
        <CardContent className="p-4">
          <p className="text-sm text-muted-foreground">Reading what stands in the way…</p>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    const availability = presentBarrierAvailability(error);
    return (
      <Card className="border-border/60" data-testid="barrier-unavailable">
        <CardContent className="p-4 space-y-2">
          <p className="text-sm font-medium">{availability.title}</p>
          <p className="text-[12px] text-muted-foreground">{availability.detail}</p>
          {onRetry && availability.kind === "UNAVAILABLE" && (
            <Button variant="outline" size="sm" onClick={onRetry}>
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
              Try again
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  if (!barrier) {
    return (
      <Card className="border-border/60">
        <CardContent className="p-4">
          <p className="text-sm text-muted-foreground">
            OMNΛS has no execution record for this package. That is not the same as nothing
            standing in the way.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="execution-barrier-map">
      <ContractMismatchCallout
        received={contractVersion}
        supported={BARRIER_CONTRACTS.workPackages}
      />

      {/* A tally of backend-given states, in the vocabulary's fixed order.
          Never sorted by size — sorting by size reads as priority. */}
      {barrier.hasInstances && (
        <div className="flex flex-wrap gap-1.5" data-testid="barrier-tally">
          {barrier.tally
            .filter((t) => t.count > 0)
            .map((t) => (
              <span
                key={t.state}
                className="inline-flex items-center gap-1 text-[11px] text-muted-foreground"
              >
                <RuntimeStateBadge state={t.state} />
                <span className="tabular-nums">{t.count}</span>
              </span>
            ))}
        </div>
      )}

      {!barrier.hasInstances && (
        <Card className="border-border/60">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">
              No execution work is linked to this package yet, so OMNΛS cannot say what would
              stand in its way. This is an absence of records, not an all-clear.
            </p>
          </CardContent>
        </Card>
      )}

      {barrier.constraints.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-muted-foreground">
            What holds this work — in the order OMNΛS returned, which is for attention and is not
            a priority order
          </p>
          {barrier.constraints.map((c) => (
            <ConstraintRow key={c.constraintKey} constraint={c} />
          ))}
        </div>
      )}

      {barrier.conflicts.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-xs font-medium text-amber-400">Disputed — resolve the citations</p>
          {barrier.conflicts.map((c) => (
            <ReducedRow
              key={c.constraintKey}
              constraint={c}
              note="Contradictory sources. No leverage figure is offered for a disputed obligation."
            />
          ))}
        </div>
      )}

      {barrier.unknownOnly.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-xs font-medium text-muted-foreground">
            Holding only unanswered conditions
          </p>
          {barrier.unknownOnly.map((c) => (
            <ReducedRow
              key={c.constraintKey}
              constraint={c}
              note="Resolving this cannot be promised to unblock anything, because what it holds is unverified."
            />
          ))}
        </div>
      )}

      {barrier.instances.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-muted-foreground">Work fronts</p>
          {barrier.instances.map((i) => (
            <InstanceCard key={i.instanceId} instance={i} />
          ))}
        </div>
      )}

      <MissingInformationCallout items={barrier.missingInformation} />
    </div>
  );
};

export default ExecutionBarrierMap;
